import { type FC, useMemo } from 'react';

import {
  FIELD_KEYFRAMES,
  FIELD_MEDIA_TYPE,
  FIELD_TITLE,
  FIELD_TYPES,
} from '@iconik-io/shared-lib/src/constants/search';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import Ellipsis from '../../../components/ellipsis/ellipsis';
import { Link } from '../../../components/link/link';
import { assetRelationsDelete } from '../../../ducks/asset_relations/actions';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import TableView from '../../../ui/table/table';
import type { Column } from '../../../ui/table/types';
import RelationControls from './relation_controls/relation_controls';

import styles from '../asset_relations.module.css';

type Props = {
  items: SearchItemEntity[];
  sourceId: string;
  label: string;
};

const AssetRelationsTable: FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const breakpoint = useAppSelector((state) => state.displayReducer.breakpoint);

  const columns = useMemo(() => {
    const renderColumns: Column<SearchItemEntity>[] = [];

    renderColumns.push({
      name: FIELD_KEYFRAMES,
      type: FIELD_TYPES.THUMBNAIL,
    });

    renderColumns.push({
      name: FIELD_TITLE,
      type: FIELD_TYPES.CUSTOM_COMPONENT,
      component: ({ item }) => {
        const relation = item.relations?.[0];
        return (
          <Ellipsis componentProps={{ className: styles.cell }}>
            <Link className={styles.link} to={`/asset/${item.id}`}>
              {item.title}
            </Link>
            {relation?.description && (
              <div className={styles.description}>{relation.description}</div>
            )}
          </Ellipsis>
        );
      },
      style: { width: '70%', maxWidth: '100px' },
    });

    if (breakpoint >= 3) {
      renderColumns.push({
        name: FIELD_MEDIA_TYPE,
        type: FIELD_TYPES.MEDIA_ICON,
        style: { width: '1%' },
      });
    }

    renderColumns.push({
      name: 'id',
      type: FIELD_TYPES.CUSTOM_COMPONENT,
      component: ({ item }) => {
        const relationItem = item.relations?.[0];

        if (
          relationItem?.relation_type === 'SUBCLIP_PARENT' ||
          relationItem?.relation_type === 'SUBCLIP_SIBLING' ||
          relationItem?.relation_type === 'SUBCLIP_PARENT_CHILD'
        ) {
          return null;
        }
        return (
          <RelationControls
            onDelete={(source: string, target: string, relation: string) =>
              dispatch(assetRelationsDelete(source, target, relation))
            }
            searchItem={item}
            label={props.label}
            sourceId={props.sourceId}
          />
        );
      },
      style: { width: '10%' },
    });
    return renderColumns;
  }, [props.sourceId, props.label, breakpoint]);

  return (
    <TableView
      items={props.items}
      renderColumns={columns}
      showHeader={false}
      aria={{
        label: `${props.label} relations`,
      }}
    />
  );
};

export default AssetRelationsTable;
